/**
 * <preorder-purchase-guard>
 *
 * Rendered inside the PDP product form ONLY on preorder-tagged products
 * (see sections/main-product-pdp.liquid). Cancels the theme's native
 * add-to-cart submit so a pre-order item can enter the cart ONLY through
 * PreProduct's own flow, which attaches the 50% deposit selling plan.
 * Also unlocks the ATC button once PreProduct has bound to it (see
 * "ATC lock/unlock" below) — the button is server-rendered locked so
 * shoppers can't click it before PreProduct is ready to react.
 *
 * Why the submit guard exists:
 * The theme product form has no selling_plan field, so a native Dawn submit
 * (product-form.js -> /cart/add.js) adds the pre-order variant at FULL price
 * with no deposit terms. PreProduct attaches the deposit only via a JS click
 * handler on the button and adds the line via its own fetch(). That handler
 * loses a load-timing race when the shopper clicks before PreProduct's
 * external embed binds, presses Enter in the quantity field, or PreProduct
 * fails to load at all — each of which let the native path add a full-price
 * pre-order line (the chargeback exposure this workstream exists to close).
 *
 * Mechanism:
 * Dawn binds its submit handler in the BUBBLE phase (product-form.js). This
 * guard listens in the CAPTURE phase, so it runs first and cancels the event
 * before Dawn's handler can fire. PreProduct adds via fetch(), not this form
 * submit, so its deposit flow is unaffected. If PreProduct never loads,
 * clicking adds NOTHING (safe) instead of a full-price line.
 *
 * ATC lock/unlock:
 * Live-verified (2026-07-21, theme dev + PreProduct embed active) that
 * PreProduct does NOT adopt the theme's button in place — it clones a fresh,
 * already-enabled button (carrying `data-native-pre-order-btn`), inserts it
 * immediately before the theme's original, and hides the original
 * (`style="display:none"`) while marking it `data-native-pre-order-btn-added`.
 * So the button the shopper actually sees flips from "our locked original" to
 * "PreProduct's plain, already-bound clone" the instant PreProduct is ready —
 * that swap IS the unlock, no JS required for it. This guard just watches the
 * ORIGINAL node (the one it locked) for either attribute — `-added` for the
 * clone-and-hide behavior actually observed, or the bare attribute in case a
 * future PreProduct version adopts truly in place — and mirrors an unlock
 * onto it for hygiene (harmless no-op once it's hidden; correct if adoption
 * ever happens in place instead). A ~8s fallback timer unlocks the button
 * regardless, in case PreProduct is slow, down, or changes this mechanism
 * again — safe either way, since the capture-phase submit guard above blocks
 * a native full-price add unconditionally; the fallback only prevents a
 * permanently dead button when PreProduct never shows up at all.
 */
if (!customElements.get('preorder-purchase-guard')) {
  const UNLOCK_FALLBACK_MS = 8000;

  customElements.define(
    'preorder-purchase-guard',
    class PreorderPurchaseGuard extends HTMLElement {
      connectedCallback() {
        this.form = this.closest('form');
        if (!this.form) return;
        this.blockNativeSubmit = this.blockNativeSubmit.bind(this);
        // Capture phase: run before Dawn's bubble-phase submit handler.
        this.form.addEventListener('submit', this.blockNativeSubmit, true);

        this.watchAtcButton();
      }

      disconnectedCallback() {
        if (this.form && this.blockNativeSubmit) {
          this.form.removeEventListener('submit', this.blockNativeSubmit, true);
        }
        if (this.atcObserver) this.atcObserver.disconnect();
        if (this.unlockFallbackTimer) clearTimeout(this.unlockFallbackTimer);
      }

      blockNativeSubmit(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }

      watchAtcButton() {
        const button = this.form.querySelector('[name="add"]');
        if (!button || button.getAttribute('aria-disabled') !== 'true') return;

        const isBound = () =>
          button.hasAttribute('data-native-pre-order-btn') ||
          button.hasAttribute('data-native-pre-order-btn-added');

        const unlock = () => {
          if (this.atcObserver) this.atcObserver.disconnect();
          if (this.unlockFallbackTimer) clearTimeout(this.unlockFallbackTimer);
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          const spinner = button.querySelector('.loading-overlay__spinner');
          if (spinner) spinner.classList.add('hidden');
        };

        if (isBound()) {
          unlock();
          return;
        }

        this.atcObserver = new MutationObserver(() => {
          if (isBound()) unlock();
        });
        this.atcObserver.observe(button, {
          attributes: true,
          attributeFilter: ['data-native-pre-order-btn', 'data-native-pre-order-btn-added'],
        });

        this.unlockFallbackTimer = setTimeout(unlock, UNLOCK_FALLBACK_MS);
      }
    }
  );
}
